import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandL, LabelM } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { currentUser as currentUserFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import {
  importantNotificationCategories,
  notificationCategories,
} from '@sorare/core/src/lib/notifications';

import type {
  ConfirmEmailMutation,
  ConfirmEmailMutationVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  position: relative;
  width: var(--100vw);
  height: var(--100vh);
  isolation: isolate;
  padding: var(--quadruple-unit);
`;
const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const confirmEmailMutation = gql`
  mutation ConfirmEmailMutation(
    $input: confirmEmailInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = false
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    confirmEmail(input: $input) {
      currentUser {
        slug
        email
        ...CurrentUserProvider_currentUser
      }
      errors {
        message
        code
      }
    }
  }
  ${currentUserFragment}
` as TypedDocumentNode<ConfirmEmailMutation, ConfirmEmailMutationVariables>;

export const ConfirmEmail = () => {
  const { updateQuery } = useConfigContext();
  const navigate = useNavigate();
  const token = useQueryString('token');
  const { sport } = useSportContext();
  const [confirm, { loading, called }] = useMutation(confirmEmailMutation, {
    showErrorsWithSnackNotification: true,
  });

  useEffect(() => {
    if (!loading && token && !called) {
      confirm({
        variables: {
          input: { token },
          sport,
          notificationCategories,
          importantNotificationCategories,
        },
      }).then(({ data }) => {
        const user = data?.confirmEmail?.currentUser;
        if (user) {
          updateQuery(user);
        }
      });
    }
  }, [loading, called, confirm, token, sport, updateQuery, navigate]);

  if (called && !loading) {
    return (
      <Root center className="text-center" gap={2}>
        <StyledTextureBackground background="wave1" color="var(--c-blue-400)" />
        <Vertical center>
          <StarBall />
          <BrandL>
            <FormattedMessage
              id="ConfirmEmail.emailConfirmed"
              defaultMessage="Email confirmed!"
            />
          </BrandL>
        </Vertical>
        <LabelM color="var(--c-nd-600)" bold>
          <FormattedMessage
            id="ConfirmEmail.emailConfirmedDescription"
            defaultMessage="You can now close this window."
          />
        </LabelM>
      </Root>
    );
  }
  return <LoadingIndicator fullScreen />;
};
